import {
  ProjectCreateRequest,
  ProjectDuplicateRequest,
  ProjectEditRequest,
} from '~/common/entities/Project/ProjectRequest';
import {
  QueryKey,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { Project } from '~/common/entities/Project/ProjectTypes';
import { IdProvider, unwrap } from '../common/apiCommons';
import axios, { AxiosError } from 'axios';

const ProjectQueries = {
  async getProject(project: IdProvider) {
    if (!project?.id) {
      return null;
    }
    const res = await axios.get<Project>(`/project/${project.id}/`);
    return unwrap(res);
  },
  async getProjects() {
    const res = await axios.get<Project[]>('/projects/');
    return unwrap(res);
  },
  async getProjectTags() {
    const res = await axios.get<{ tag: string }[]>('/project/tags/');
    return unwrap(res);
  },
};

const ProjectMutations = {
  async createProject(request: ProjectCreateRequest) {
    const res = await axios.post<Project>('/project/', request);
    return unwrap(res);
  },
  async editProject(r: ProjectEditRequest & IdProvider) {
    if (!r?.id) {
      return null;
    }
    const { id, ...request } = r;
    const res = await axios.put<Project>(`/project/${id}/`, request);
    return unwrap(res);
  },
  async deleteProject(project: IdProvider) {
    if (!project?.id) {
      return;
    }
    const res = await axios.delete<void>(`/project/${project.id}/`);
    return unwrap(res);
  },
  async duplicateProject(r: ProjectDuplicateRequest & IdProvider) {
    if (!r?.id) {
      return;
    }
    const { id, ...request } = r;
    const res = await axios.post<Project>(`/project/${id}/duplicate/`, request);
    return unwrap(res);
  },
  async toggleOption(option: IdProvider) {
    if (!option?.id) {
      return;
    }
    const res = await axios.post<void>(`/project/${option.id}/option/`);
    return unwrap(res);
  },
};

export enum ProjectTags {
  PROJECT = 'PROJECT',
}

const ProjectHooks = {
  useProjectQuery(
    project: IdProvider,
    options?: UseQueryOptions<Project | null>,
  ) {
    const result = useQuery({
      queryKey: [ProjectTags.PROJECT, project?.id],
      queryFn: () => ProjectQueries.getProject({ id: project?.id }),

      cacheTime: 1000 * 60 * 5,
      ...(options ?? {}),
    });
    const queryClient = useQueryClient();
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([ProjectTags.PROJECT, project?.id]),
    ];
    return QueryResultAndInvalidate;
  },
  useProjectsQuery2<T = Project[]>(
    options?: Omit<
      UseQueryOptions<Project[], AxiosError, T>,
      'queryFn' | 'queryKey' | 'cacheTime'
    >,
  ) {
    return useQuery({
      ...options,
      queryKey: [ProjectTags.PROJECT] as QueryKey,
      queryFn: ProjectQueries.getProjects,
      cacheTime: 1000 * 60 * 5,
    });
  },
  useProjectsQuery(options?: UseQueryOptions<Project[]>) {
    const result = useQuery({
      queryKey: [ProjectTags.PROJECT],
      queryFn: ProjectQueries.getProjects,
      cacheTime: 1000 * 60 * 5,
      ...(options ?? {}),
    });
    const queryClient = useQueryClient();
    const QueryResultAndInvalidate: [typeof result, () => Promise<void>] = [
      result,
      () => queryClient.invalidateQueries([ProjectTags.PROJECT]),
    ];
    return QueryResultAndInvalidate;
  },
  useProjectTags<T = { tag: string }[]>(
    options?: UseQueryOptions<{ tag: string }[], unknown, T>,
  ) {
    return useQuery({
      queryKey: [ProjectTags.PROJECT, 'tags'],
      queryFn: ProjectQueries.getProjectTags,
      ...(options ?? {}),
    });
  },
  useCreateProjectMutation() {
    const queryClient = useQueryClient();
    return useMutation(ProjectMutations.createProject, {
      async onSuccess() {
        await queryClient.invalidateQueries([ProjectTags.PROJECT]);
      },
    });
  },
  useEditProjectMutation() {
    const queryClient = useQueryClient();
    return useMutation(ProjectMutations.editProject, {
      async onSuccess(project) {
        if (!project?.id) {
          return;
        }
        await queryClient.invalidateQueries([ProjectTags.PROJECT]);
        await queryClient.invalidateQueries([ProjectTags.PROJECT, project.id]);
      },
    });
  },
  useDeleteProjectMutation() {
    const queryClient = useQueryClient();
    return useMutation(ProjectMutations.deleteProject, {
      async onSuccess() {
        await queryClient.invalidateQueries([ProjectTags.PROJECT]);
      },
    });
  },
  useDuplicateProjectMutation() {
    const queryClient = useQueryClient();
    return useMutation(ProjectMutations.duplicateProject, {
      async onSuccess() {
        await queryClient.invalidateQueries([ProjectTags.PROJECT]);
      },
    });
  },
  useToggleOptionMutation() {
    const queryClient = useQueryClient();
    return useMutation(ProjectMutations.toggleOption, {
      onSuccess(_data, params) {
        if (!params?.id) {
          return;
        }
        const projects: Project[] | undefined = queryClient.getQueryData([
          ProjectTags.PROJECT,
        ]);
        const project: Project | undefined = queryClient.getQueryData([
          ProjectTags.PROJECT,
          params.id,
        ]);
        if (projects) {
          queryClient.setQueryData(
            [ProjectTags.PROJECT],
            [
              ...projects.map((p) =>
                p.id === params.id ? { ...p, is_option: !p.is_option } : p,
              ),
            ],
          );
        }
        if (project) {
          queryClient.setQueryData([ProjectTags.PROJECT, params.id], {
            ...project,
            is_option: !project.is_option,
          });
        }
      },
    });
  },
};

const ProjectApi = {
  queries: ProjectQueries,
  mutations: ProjectMutations,
  hooks: ProjectHooks,
};

export default ProjectApi;
